import store from '@/store';
import axios from 'axios';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Endpoints } from '@/ts/store/Constants';
import VariantData from '@/ts/VariantData';
import VariantMediaData from '@/ts/VariantMediaData';
import { VariantDataResponse, VariantMediaDataResponse } from '@/ts/HococoInterfaces';
import Projects from './Projects';
import { FloorPlanImageData } from '@/ts/HococoInterfaces';
import ErrorResponse from '@/ts/ErrorResponse';
import difference from 'lodash/difference';
import PDFPreviewerHelper from '@/ts/helpers/PDFPreviewerHelper';

@Module({name: 'Variants', namespaced: true})
export default class Variants extends VuexModule {
    public variants: VariantData[] = [];
    public variantsMap: { [index: number]: number } = {};
    public variantId: number;
    public variantMedia: VariantMediaData[] = [];
    public variantMediaMap: { [index: number]: number } = {};
    public variantMediaData: VariantMediaData | null = null;
    public cachedVariant: VariantData = null;
    public activeVariantId: number = null;
    public editFormVisible: boolean = false;
    public uploadFloorPlanFormVisible: boolean = false;
    public floorPlanBase64ImageData: FloorPlanImageData = null;
    public newFloorPlanSelected: boolean = false;
    public currentEditionIndex: number = -1;

    @Mutation
    public RESET_VARIANTS_STORE_STATE(): void {
        this.variants                   = [];
        this.variantsMap                = {};
        this.variantId                  = null;
        this.variantMedia               = [];
        this.variantMediaMap            = {};
        this.variantMediaData           = null;
        this.cachedVariant              = null;
        this.activeVariantId            = null;
        this.editFormVisible            = false;
        this.uploadFloorPlanFormVisible = false;
        this.floorPlanBase64ImageData   = null;
        this.newFloorPlanSelected       = false;
        this.currentEditionIndex        = -1;
    }

    get allVariants() {
        return this.variants;
    }

    get projectsVariants() {
        return ((projectIds: number[]) => {
            return this.variants.filter((variant) => projectIds.includes(variant.projectId));
        });
    }


    get allVariantsForCurrentSelection() {
        if (store.state.Projects.activeProjectId === null) {
            return this.variants;
        }

        return this.variants.filter((variant) => {
            return variant.projectId === store.state.Projects.activeProjectId;
        });
    }

    get uploadFloorPlanMenuVisible(): boolean {
        return this.uploadFloorPlanFormVisible;
    }

    get getNewFloorPlanSelected(): boolean {
        return this.newFloorPlanSelected;
    }

    get allVariantMedia() {
        return this.variantMedia;
    }

    get getVariantMedia() {
        return (fileId: number) => {
            if (fileId === Variants.state.variantMedia.id) {
                return Variants.state.variantMedia;
            }

            return Variants.state.variantMedia[Variants.state.variantMediaMap[fileId]];
        };
    }

    get editMenuVisible(): boolean {
        return this.editFormVisible;
    }

    get activeVariant() {
        if (this.activeVariantId === null) {
            return null;
        }

        return this.variants[this.variantsMap[this.activeVariantId]];
    }

    get getVariantId() {
        if (this.activeVariantId === null) {
            return null;
        }

        return this.variants[this.variantsMap[this.activeVariantId]].id;
    }

    get getActiveVariantMedia() {
        if (this.activeVariantId === null) {
            return null;
        }

        return this.variants[this.variantsMap[this.activeVariantId]].media;
    }

    get getVariant() {
        return ((variantId: number) => {
            return this.variants[this.variantsMap[variantId]];
        });
    }

    get getVariantProject() {
        if (this.activeVariantId === null) {
            return null;
        }

        for (const project of Projects.state.projects) {
            if (project.selected === true) {
                this.variants[this.variantsMap[this.activeVariantId]].projectId = project.id;
            }
        }

        return this.variants[this.variantsMap[this.activeVariantId]].projectId;
    }

    get getVariantName() {
        if (this.activeVariantId === null) {
            return null;
        }

        return this.variants[this.variantsMap[this.activeVariantId]].name;
    }

    get getVariantRooms() {
        if (this.activeVariantId === null) {
            return null;
        }

        return this.variants[this.variantsMap[this.activeVariantId]].rooms;
    }

    get getVariantArea() {
        if (this.activeVariantId === null) {
            return null;
        }

        return this.variants[this.variantsMap[this.activeVariantId]].area;
    }

    get getVariantDescription() {
        if (this.activeVariantId === null) {
            return null;
        }

        return this.variants[this.variantsMap[this.activeVariantId]].description;
    }

    get getVariantFileName() {
        if (this.activeVariantId === null) {
            return null;
        }

        return Variants.state.variantMedia.filename;
    }

    get variantHasFloorPlan() {
        if (this.activeVariantId === null) {
            return null;
        }

        return this.variants[this.variantsMap[this.activeVariantId]].hasFloorPlan;
    }

    @Action
    public INITIATE_VARIANT() {
        store.commit(Mutations.INITIATE_NEW_VARIANT);
    }

    @Action
    public FETCH_VARIANT_PREVIEW_MEDIA(data: any) {
        axios.get(Endpoints.Admin.Variants.VARIANT_MEDIA_PREVIEW(data.variantId, data.mediaId))
            .then((response) => {
                new PDFPreviewerHelper(response.data).openPdf();
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_VARIANTS() {
        return axios.get(Endpoints.Admin.Variants.VARIANTS)
            .then((response) => {
                store.commit(Mutations.ADD_VARIANTS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_VARIANT_MEDIA() {
        const variant = Variants.state.variants[
                            Variants.state.variantsMap[
                                Variants.state.activeVariantId]];

        axios.get(Endpoints.Admin.Variants.VARIANT_MEDIA(variant.id))
            .then((response) => {
                Variants.state.variants[Variants.state.variantsMap[variant.id]].media = [];

                if (response.data != null && response.data.length > 0) {
                    store.commit(Mutations.ADD_VARIANT_MEDIA, response.data);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
        return;
    }

    @Action
    public POST_VARIANTS() {
        const headers = {
            headers: {
                'Content-Type': 'multipart-data',
            },
        };

        const variant = Variants.state.variants[
                            Variants.state.variantsMap[
                                Variants.state.activeVariantId]];

        const requestPayload = new FormData();
        requestPayload.append('projectId', variant.projectId);
        requestPayload.append('name', variant.name);
        requestPayload.append('description', variant.description);
        requestPayload.append('area', variant.area);
        requestPayload.append('rooms', variant.rooms);

        if (variant.media.length > 0) {
            variant.media.forEach((mediaItem: VariantMediaData, index: number) => {
                if (mediaItem.file !== null && mediaItem.id < 0) {
                    requestPayload.append('filesToUpload[' + index + ']', mediaItem.file);
                }
            });
        }

        axios.post(Endpoints.Admin.Variants.VARIANTS, requestPayload, headers)
            .then((response) => {
                store.commit(Mutations.UPDATE_PERSISTED_VARIANT, response.data.id);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public EDIT_VARIANT() {
        const variant = Variants.state.variants[
                            Variants.state.variantsMap[
                                Variants.state.activeVariantId]];

        if (variant.dataEquals(Variants.state.cachedVariant)) {
            return store.commit(Mutations.HIDE_EDIT_VARIANT_FORM);
        }

        const headers = {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        };

        const requestPayload = new FormData();

        requestPayload.append('_method', 'PATCH');
        requestPayload.append('projectId', variant.projectId);
        requestPayload.append('name', variant.name);
        requestPayload.append('description', variant.description);
        requestPayload.append('area', variant.area);
        requestPayload.append('rooms', variant.rooms);

        if (variant.media.length > 0) {
            variant.media.forEach((mediaItem: VariantMediaData) => {
                if (mediaItem.file !== null && !mediaItem.toBeDeleted && mediaItem.id < 0) {
                    requestPayload.append('filesToUpload[]', mediaItem.file);
                }

                if (mediaItem.id > 0 && mediaItem.toBeDeleted) {
                    requestPayload.append('filesToBeDeleted[]', mediaItem.id.toString());
                }
            });
        }

        axios.post(Endpoints.Admin.Variants.SINGLE_VARIANT(variant.id), requestPayload, headers)
            .then((response) => {
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public UPLOAD_FLOOR_PLAN() {
        axios.post(Endpoints.Admin.Variants.FLOOR_PLAN(Variants.state.activeVariantId), Variants.state.floorPlanBase64ImageData)
            .then((response) => {
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public DELETE_UNIT_TYPE(data: any) {
        axios.delete(Endpoints.Admin.Variants.SINGLE_VARIANT(data.variantId))
            .then((response) => {
                if (response.data) {
                    store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
                    store.commit(Mutations.REMOVE_VARIANT, data.variantId);
                }
            })
            .catch((error) => {
                if (error.response) {
                    store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Mutation
    public REMOVE_VARIANT(variantId: number) {
        this.variants = this.variants.filter((variant: any) => {
            return variant.id !== variantId;
        });

        this.variantsMap = {};
        for (const [i, event] of this.variants.entries()) {
            this.variantsMap[event.id] = i;
        }
    }

    @Mutation
    public SAVE_FLOOR_PLAN_BASE64_IMAGE_DATA(base64Image: FloorPlanImageData) {
        this.floorPlanBase64ImageData = base64Image;
    }

    @Mutation
    public ADD_VARIANT(variant: VariantDataResponse): void {
        let arrayIndex = null;

        if (!this.variantsMap.hasOwnProperty(variant.id)) {
            arrayIndex = this.variants.push(new VariantData(
                variant.id,
                variant.projectId,
                variant.projectName,
                variant.name,
                variant.area,
                variant.rooms,
                variant.description,
            )) - 1;

            this.variantsMap[variant.id] = arrayIndex;
        }
    }

    @Mutation
    public ADD_VARIANTS(variantData: VariantDataResponse[]): void {
        if (!variantData.length) {
            this.variants    = [];
            this.variantsMap = {};
        }

        for (const variant of variantData) {
            let arrayIndex = null;

            if (!this.variantsMap.hasOwnProperty(variant.id)) {
                arrayIndex = this.variants.push(new VariantData(
                    variant.id,
                    variant.projectId,
                    variant.projectName,
                    variant.name,
                    variant.area,
                    variant.rooms,
                    variant.description,
                    variant.hasFloorPlan !== null,
                )) - 1;

                this.variantsMap[variant.id] = arrayIndex;
            } else {
                arrayIndex            = this.variantsMap[variant.id];
                const existingVariant = this.variants[arrayIndex];

                if (existingVariant) {
                    existingVariant.updateLocalVariantData(variant);
                }
            }
        }

        const responseVariantsIds = variantData.map((v) => v.id);
        const localVariantsIds    = this.variants.map((v) => v.id);
        const variantsToRemove    = difference(localVariantsIds, responseVariantsIds);

        for (const variant of variantsToRemove) {
            store.commit(Mutations.REMOVE_VARIANT, variant);
        }
    }

    @Mutation
    public ADD_VARIANT_MEDIA(variantMediaData: VariantMediaDataResponse[]): void {
        for (const media of variantMediaData) {
            if (!Variants.state.variants[Variants.state.variantsMap[media.variantId]].media.hasOwnProperty(media.id)) {
                Variants.state.variants[Variants.state.variantsMap[media.variantId]].addMedia(new VariantMediaData(
                    media.id,
                    media.variantId,
                    media.typeId,
                    media.storageGuid,
                    media.filename,
                    true,
                ));
            }
        }
    }

    @Mutation
    public ADD_VARIANT_SELECTED_MEDIA(documentData: any): void {
        Variants.state.variants[Variants.state.variantsMap[Variants.state.activeVariantId]].addMedia(new VariantMediaData(
            this.currentEditionIndex--,
            this.activeVariantId,
            null,
            '',
            documentData.documentName,
            false,
            documentData.document,
        ));
    }

    @Mutation
    public UPDATE_VARIANT_PROJECT_ID(value: number): void {
        this.variants[this.variantsMap[this.activeVariantId]].projectId = value;
    }

    @Mutation
    public UPDATE_VARIANT_NAME(value: string): void {
        this.variants[this.variantsMap[this.activeVariantId]].name = value;
    }

    @Mutation
    public UPDATE_VARIANT_ROOMS(value: number): void {
        this.variants[this.variantsMap[this.activeVariantId]].rooms = value;
    }

    @Mutation
    public UPDATE_VARIANT_AREA(value: string): void {
        this.variants[this.variantsMap[this.activeVariantId]].area = value;
    }

    @Mutation
    public UPDATE_VARIANT_DESCRIPTION(value: string): void {
        this.variants[this.variantsMap[this.activeVariantId]].description = value;
    }

    @Mutation
    public UPDATE_VARIANT_FILE_NAME(value: string): void {
        Variants.state.variantMedia = this.variants[this.variantsMap[this.activeVariantId]].media;
        Variants.state.variantMedia.filename = value;
    }

    @Mutation
    public REMOVE_DOCUMENT_FROM_ACTIVE_VARIANT(itemId: number) {
        this.variants[this.variantsMap[this.activeVariantId]].removeMedia(itemId);
    }

    @Mutation
    public UPDATE_ACTIVE_VARIANT(variantId: number): void {
        this.activeVariantId = variantId;
    }

    @Mutation
    public EDIT_ACTIVE_VARIANT(): void {
        this.cachedVariant = JSON.parse(JSON.stringify(this.variants[this.variantsMap[this.activeVariantId]]));
    }

    @Mutation
    public INITIATE_NEW_VARIANT() {
        const activeIndex = this.variants.push(new VariantData(-1)) - 1;
        this.variantsMap[-1] = activeIndex;
        this.activeVariantId = -1;
    }

    @Mutation
    public UPDATE_PERSISTED_VARIANT(newVariantId: number) {
        const currentArrayIndex = this.variantsMap[this.activeVariantId];
        this.activeVariantId = null;
        this.variantsMap[newVariantId] = currentArrayIndex;
        this.variants[currentArrayIndex].id = newVariantId;
        delete this.variantsMap[-1];
    }

    @Mutation
    public CANCEL_EDIT_VARIANT() {
        this.variants[this.variantsMap[this.activeVariantId]].cloneData(this.cachedVariant);

        this.cachedVariant = null;
    }

    @Mutation
    public SHOW_EDIT_VARIANT_FORM() {
        this.editFormVisible = true;
    }

    @Mutation
    public HIDE_EDIT_VARIANT_FORM() {
        this.editFormVisible = false;
    }

    @Mutation
    public SHOW_UPLOAD_FLOOR_PLAN_FORM() {
        this.uploadFloorPlanFormVisible = true;
    }

    @Mutation
    public HIDE_UPLOAD_FLOOR_PLAN_FORM() {
        this.uploadFloorPlanFormVisible = false;
    }

    @Mutation
    public EDIT_FLOOR_PLAN() {
        this.newFloorPlanSelected = true;
    }
}

